import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, Target, CheckSquare, Radar, Megaphone, BarChart3, UserCog, LogOut, ChevronLeft, Menu } from 'lucide-react';
import ThemeSwitcher from './ThemeSwitcher';
import NotificationsBell from './NotificationsBell';

const NAV = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/leads', icon: Target, label: 'Leads' },
  { path: '/campaigns', icon: Megaphone, label: 'Campaigns' },
  { path: '/tasks', icon: CheckSquare, label: 'Follow-ups' },
  { path: '/visitors', icon: Radar, label: 'Visitors' },
  { path: '/reports', icon: BarChart3, label: 'Reports' },
  { path: '/employees', icon: Users, label: 'Employees' },
  { path: '/content', icon: FileText, label: 'Content' },
  { path: '/my-profile', icon: UserCog, label: 'My Profile' },
];
const TITLES = { '/dashboard': 'Dashboard', '/leads': 'Leads', '/campaigns': 'Campaigns', '/tasks': 'Follow-ups', '/visitors': 'Visitors', '/reports': 'Reports', '/employees': 'Employees', '/content': 'Content', '/my-profile': 'My Profile' };

export default function Layout() {
  const [open, setOpen] = useState(true);
  const [drawer, setDrawer] = useState(false); // mobile only
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { setDrawer(false); }, [location.pathname]); // close the drawer on navigate

  // PWA: while in the admin area, point the install manifest at the admin one so
  // "Add to Home Screen" installs a "DFC Admin" app that opens on /dashboard.
  // Purely metadata — no service worker, so it can't cache or affect the live app.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute('href') : null;
    if (link) link.setAttribute('href', '/manifest-admin.json');
    let apple = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!apple) { apple = document.createElement('meta'); apple.setAttribute('name', 'apple-mobile-web-app-title'); document.head.appendChild(apple); }
    apple.setAttribute('content', 'DFC Admin');
    return () => { if (link && prev) link.setAttribute('href', prev); }; // restore on leaving admin
  }, []);

  const logout = () => { ['token', 'crm_token', 'crm_user'].forEach((k) => localStorage.removeItem(k)); navigate('/login'); };

  return (
    <div className="flex h-screen" style={{ background: 'radial-gradient(1200px 600px at 100% -10%, var(--bg-a) 0%, var(--bg-b) 45%)' }}>
      {drawer && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setDrawer(false)} aria-hidden="true" />}

      <aside className={`w-[248px] ${open ? 'lg:w-[248px]' : 'lg:w-[82px]'} shrink-0 flex flex-col transition-transform lg:transition-all duration-300 text-white rounded-3xl overflow-hidden
        fixed inset-y-3 left-3 z-40 ${drawer ? 'translate-x-0' : '-translate-x-[115%]'}
        lg:relative lg:inset-auto lg:left-auto lg:translate-x-0 lg:z-auto lg:m-3`}
        style={{ background: 'var(--sidebar)', boxShadow: '0 30px 60px -20px rgba(0,0,0,.5)' }}>
        <div className="pointer-events-none absolute -top-16 -right-10 w-52 h-52 rounded-full" style={{ background: 'radial-gradient(circle,var(--sglow),transparent 70%)' }} />

        <div className="relative h-[70px] flex items-center gap-3 px-5">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center p-1.5 shrink-0 shadow-lg">
            <img src="/dfci-logo.png" alt="DFC" className="max-w-full max-h-full object-contain" />
          </div>
          {open && <div className="leading-tight"><div className="font-extrabold text-[16px] tracking-tight">DFC</div><div className="text-[10px] text-white/50 font-medium tracking-wide">LEAD CLOUD</div></div>}
        </div>

        {open && <div className="text-[10px] font-semibold text-white/30 uppercase tracking-wider px-6 mt-3 mb-1">Menu</div>}
        <nav className="relative flex-1 px-3 space-y-1.5 mt-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} title={item.label}
                className={`group relative flex items-center gap-3 px-3.5 py-3 rounded-2xl text-[14px] font-semibold transition-all ${active ? '' : 'text-white/55 hover:text-white'}`}
                style={active ? { background: 'var(--pill)', color: 'var(--pill-text)', boxShadow: '0 8px 20px -6px rgba(0,0,0,.35)' } : {}}>
                {!active && <span className="absolute inset-0 rounded-2xl bg-white/0 group-hover:bg-white/10 transition-colors" />}
                <Icon size={19} className="relative shrink-0" />
                {open && <span className="relative">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="relative p-3">
          {open && (
            <div className="rounded-2xl p-3 mb-2" style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.09)' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: 'var(--pill)', color: 'var(--pill-text)' }}>A</div>
                <div className="min-w-0"><div className="text-[13px] font-bold truncate">Admin</div><div className="text-[10px] text-white/50">Administrator</div></div>
              </div>
            </div>
          )}
          <button onClick={logout} className="flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-[13px] font-semibold text-white/55 hover:text-white hover:bg-white/10 w-full transition-colors">
            <LogOut size={18} />{open && <span>Sign out</span>}
          </button>
        </div>

        <button onClick={() => setOpen(!open)}
          className="absolute -right-3 top-[62px] w-6 h-6 rounded-full bg-white text-slate-700 shadow-lg hidden lg:flex items-center justify-center border border-slate-200 z-10">
          <ChevronLeft size={15} className={`transition-transform ${open ? '' : 'rotate-180'}`} />
        </button>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-[70px] flex items-center justify-between px-4 lg:px-7 shrink-0 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setDrawer(true)} aria-label="Open menu"
              className="lg:hidden shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border"
              style={{ borderColor: 'var(--border)', background: 'var(--card)', color: 'var(--text)' }}>
              <Menu size={18} />
            </button>
            <div className="min-w-0">
            <h2 className="text-[22px] font-extrabold tracking-tight leading-none" style={{ color: 'var(--heading)' }}>{TITLES[location.pathname] || 'Admin'}</h2>
            <div className="text-[12px] mt-1 font-medium truncate" style={{ color: 'var(--muted)' }}>Diabetik Foot Care India · Lead Management</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ThemeSwitcher />
            <NotificationsBell />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto px-4 lg:px-7 pb-7"><Outlet /></main>
      </div>
    </div>
  );
}
